import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Info, ExternalLink, ShieldCheck } from 'lucide-react';

/**
 * Displays CC0 / Public-Domain attribution for the ambient soundscape files
 * shipped with Lumera. Loads `/ambient/ATTRIBUTION.json` (generated by
 * `scripts/build_cc0_ambient.py`) and renders a per-scene credit list.
 */
const AmbientCredits = ({ triggerVariant = 'ghost', triggerSize = 'sm', es = false }) => {
  const [open, setOpen] = useState(false);
  const [credits, setCredits] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!open || loaded) return;
    fetch('/ambient/ATTRIBUTION.json', { cache: 'no-cache' })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setCredits(Array.isArray(data) ? data : []))
      .catch(() => setCredits([]))
      .finally(() => setLoaded(true));
  }, [open, loaded]);

  return (
    <>
      <Button
        type="button"
        variant={triggerVariant}
        size={triggerSize}
        className="h-7 px-2 text-xs text-muted-foreground hover:text-primary"
        onClick={() => setOpen(true)}
        data-testid="ambient-credits-btn"
      >
        <Info className="w-3.5 h-3.5 mr-1" />
        {es ? 'Créditos de audio' : 'Audio credits'}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-lg max-h-[80vh] overflow-y-auto"
          data-testid="ambient-credits-dialog"
        >
          <DialogHeader>
            <DialogTitle>{es ? 'Créditos de los paisajes sonoros' : 'Ambient Soundscape Credits'}</DialogTitle>
            <DialogDescription>
              {es
                ? 'Cada bucle es CC0 o de dominio público. No se requiere atribución, pero la incluimos abajo en gratitud a los autores originales.'
                : 'Every loop is CC0 or Public Domain. No attribution is required, but we provide it below out of gratitude to the original authors.'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-300" data-testid="ambient-cc0-badge">
            <ShieldCheck className="w-4 h-4 flex-shrink-0" />
            {es
              ? '100% CC0 / Dominio público — redistribuible sin atribución ni obligaciones de compartir-igual.'
              : '100% CC0 / Public Domain — redistributable without attribution or share-alike obligations.'}
          </div>

          {!loaded && (
            <p className="text-sm text-muted-foreground py-4">{es ? 'Cargando…' : 'Loading…'}</p>
          )}
          {loaded && credits.length === 0 && (
            <p className="text-sm text-muted-foreground py-4">
              {es ? 'No se encontró el manifiesto de atribución.' : 'No attribution manifest found.'}
            </p>
          )}
          {loaded && credits.length > 0 && (
            <ul className="divide-y divide-border text-sm">
              {credits.map((c) => (
                <li key={c.scene} className="py-3 flex flex-col gap-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <span
                      className="font-semibold capitalize"
                      data-testid={`credit-scene-${c.scene}`}
                    >
                      {c.scene}
                    </span>
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">
                      {c.license}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {c.display_title || c.source_title}
                  </p>
                  {c.artist && (
                    <p className="text-xs">
                      by <span className="font-medium">{c.artist}</span>
                    </p>
                  )}
                  {c.source_url && (
                    <a
                      href={c.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      {es ? 'Ver fuente' : 'View source'} <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}

          <p className="text-xs text-muted-foreground pt-2 border-t">
            {es
              ? 'Los bucles procedurales son creados por Lumera y liberados al dominio público (CC0). Las grabaciones de campo provienen de Wikimedia Commons bajo CC0 o dominio público. Consulta el '
              : 'Procedural loops are authored by Lumera and released into the public domain (CC0). Field recordings are fetched from Wikimedia Commons under CC0 or existing Public Domain status. See the '}
            <a
              href="/ambient/README.md"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              {es ? 'README de audio ambiental' : 'ambient README'}
            </a>{' '}
            {es ? 'para los pasos de reproducción.' : 'for reproduction steps.'}
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AmbientCredits;
